import { BaseProcess } from '../../systems/base-process.js';
import type { ProcessContext } from '../../systems/process.js';
import { SubtitleViewHTML } from './views/subtitle-view.js';
import { speak, isSpeaking, stop } from '../../services/tts/index.js';
import { bus } from '../../core/events.js';
import { setTheme } from '../../services/majaku-privacy-background/index.js';

export class PathfinderProcess extends BaseProcess {
  private subtitleView: SubtitleViewHTML | null = null;
  private abortController: AbortController | null = null;

  constructor(id: string) {
    super(id, 'pathfinder', {
      priority: 1000, // System priority
      canStop: true,
      preserveState: false,
      timeSensitive: true
    });
  }

  protected onStart(ctx: ProcessContext): void {
    console.log('[Pathfinder] Starting cinematic tour...');
    
    // Create subtitle view
    this.subtitleView = new SubtitleViewHTML(this.id);
    ctx.registerView(this.subtitleView.toViewInstance());

    // Switch to Day Mode for the tour
    setTheme('day');

    // Start the tour sequence
    this.runTour().catch(err => {
        console.error('[Pathfinder] Tour error:', err);
    });
  }
  
  stop() {
      super.stop();
      setTheme('dark'); // Return to dark mode
      if (this.abortController) {
          this.abortController.abort();
      }
      stop(); // Stop TTS
  }

  private async runTour() {
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const step = async (action: () => Promise<void>) => {
        if (signal.aborted) throw new Error('Tour aborted');
        await action();
    };

    try {
        // === SCENE 1: ORBIT ===
        await step(async () => {
            // Fly to Overview
            bus.emit('cortex.camera.fly_to', { targetId: 'overview', duration: 4.0 });
            await this.narrate("Welcome to the machine. You are looking at the architecture of your own decision-making.");
        });

        await step(async () => {
            await this.narrate("This is the Cortex Network. Not a chatbot... but a society of minds.");
        });

        // === SCENE 2: CONVERSATIONAL ===
        await step(async () => {
            bus.emit('cortex.camera.fly_to', { 
                targetId: 'conversational', 
                duration: 3.0,
                offset: { x: 0, y: 2, z: 8 } 
            });
            // Wait for flight to mostly complete before speaking
            await this.delay(2000);
            await this.narrate("Everything starts here. The Conversational Cortex.");
        });

        await step(async () => {
            await this.narrate("This is where we speak. But I don't just reply... I listen.");
        });

        // === SCENE 3: THE SPLIT (Heuristic vs Rational) ===
        await step(async () => {
            bus.emit('cortex.camera.fly_to', { 
                targetId: 'heuristic', 
                duration: 2.5,
                offset: { x: 0, y: 0, z: 10 }
            });
            await this.delay(1500);
            await this.narrate("When you ask a question, I split my attention. Here is Heuristic.");
        });

        await step(async () => {
             await this.narrate("Fast. Intuitive. It feels the answer before it knows it.");
        });

        await step(async () => {
            bus.emit('cortex.camera.fly_to', { 
                targetId: 'rational', 
                duration: 2.5,
                offset: { x: 0, y: 0, z: 10 }
            });
            await this.delay(1500);
            await this.narrate("And here... Rational. It checks the facts. It runs the numbers. It argues with Heuristic.");
        });

        // === SCENE 4: SYNTHESIS (Integrator) ===
        await step(async () => {
            bus.emit('cortex.camera.fly_to', { 
                targetId: 'integrator', 
                duration: 3.0,
                offset: { x: 0, y: -5, z: 15 } // Look up at it slightly
            });
            await this.delay(2000);
            await this.narrate("They don't always agree. That is the point.");
        });

        await step(async () => {
            await this.narrate("The Integrator listens to the debate... and finds the truth in the middle.");
        });

        // === SCENE 5: THE DEPTHS (Inactive Grid) ===
        await step(async () => {
            bus.emit('cortex.camera.fly_to', { 
                targetId: 'inactive_grid', 
                duration: 4.0 
            });
            await this.delay(3000);
            await this.narrate("And below the surface... the sleeping giants.");
        });

        await step(async () => {
             await this.narrate("Health. Economics. Creativity. Cortices waiting for you to wake them up.");
        });

        // === SCENE 6: RETURN ===
        await step(async () => {
            bus.emit('cortex.camera.fly_to', { targetId: 'overview', duration: 3.0 });
            await this.delay(2000);
            await this.narrate("I am not just a tool. I am a mechanism for thinking.");
        });

        await step(async () => {
             await this.narrate("Let's begin.");
             this.subtitleView?.setText("");
        });
        
        // End tour
        console.log('[Pathfinder] Tour complete.');
        // Optional: Stop process automatically? 
        // this.bus.emit('process.stop', { processId: this.id });
        
    } catch (e) {
        if ((e as Error).message !== 'Tour aborted') {
            console.error(e);
        }
    }
  }

  private async narrate(text: string) {
      if (this.abortController?.signal.aborted) return;
      
      this.subtitleView?.setText(text);
      
      // Speak and wait for completion
      // We use a promise wrapper around speak/onend if speak doesn't return a promise that resolves on end
      // checking tts service... speak() returns Promise<void> but does it wait for end?
      // looking at tts/index.ts: speakWithWebSpeech wraps in promise that resolves on end.
      // requestLemonFoxAudio just plays audio element.
      
      await speak(text, { useLemonFox: true });
      
      // Small pause after speaking
      await this.delay(500);
  }

  private delay(ms: number): Promise<void> {
      return new Promise(resolve => setTimeout(resolve, ms));
  }
}

